import { z } from 'zod';
import type { Dependency } from '../../dependency.ts';
import {
  addTelegramChannelInput,
  applyAnalystInput,
  archiveBookInput,
  clearBookTermsInput,
  closeRecommendationInput,
  createAnalystInput,
  decideAccessInput,
  decideAnalystInput,
  decideFollowInput,
  editAnalystFields,
  editBookInput,
  editRecommendationInput,
  mintFollowInviteInput,
  mintWebhookTokenInput,
  openBookInput,
  removeAnalystInput,
  removeChannelInput,
  removePositionInput,
  removeRecommendationInput,
  requestFollowInput,
  revokeFollowInviteInput,
  setAutopilotPausedInput,
  setBookTermsFields,
  setChannelFilingInput,
  setExitWithRecommendationInput,
  setMemberTermsFields,
  setOwnLevelsInput,
  setPlacingInput,
  submitFeedbackInput,
  unfollowInput,
} from '../../ontology/inputs.ts';
import { analystBioSchema, analystNameSchema } from '../../ontology/objects/analyst.ts';
import { bookSchema } from '../../ontology/objects/book.ts';
import { channelLabelSchema } from '../../ontology/objects/channel.ts';
import { emailSchema, namesATerm } from '../../ontology/objects/member.ts';
import { NOTIFY_KINDS } from '../../ontology/objects/notification.ts';
import { NO_TERM_GIVEN, namesAnOverride } from '../../ontology/objects/terms.ts';
import { idSchema } from '../../ontology/primitives.ts';
import { addFieldsSchema } from './add.ts';
import { ticketRefSchema } from './position.ts';
import { price, requestTokenSchema } from './primitives.ts';

const named = <N extends string>(name: N) => ({ do: z.literal(name) });

/** The websites `createVendor` paces and pauses (`src/prices/vendor.ts`), as a person names each. */
export const VENDOR_SITES = {
  nse: 'NSE',
  nsdl: 'NSDL',
  'zerodha-calendar': 'Zerodha’s calendar',
  eia: 'EIA',
  bls: 'BLS',
  bea: 'BEA',
  amfi: 'AMFI',
} as const satisfies Partial<Record<Dependency, string>>;

export type VendorSite = keyof typeof VENDOR_SITES;

const holdingIds = z.array(idSchema).min(1).max(32);

export const webActionSchema = z.discriminatedUnion('do', [
  z.object({
    ...named('exit-position'),
    positionIds: holdingIds,
    exitPrice: price,
    place: z.boolean().default(false),
    /** Units out; omitted, the whole holding. */
    quantity: z.number().int().positive().optional(),
  }),
  removePositionInput.omit({ memberId: true }).extend(named('remove-position')),
  z.object({ ...named('arm-fallback'), positionIds: holdingIds }),
  z.object({
    ...named('open-position'),
    recommendationId: idSchema,
    ...ticketRefSchema.shape,
    /** True defers to the standing placing consent; false refuses placing for this ticket only. */
    place: z.boolean().default(true),
    exitsWithRecommendation: z.boolean().default(true),
  }),
  z.object({ ...named('edit-position'), positionId: idSchema, ...ticketRefSchema.shape }),
  setOwnLevelsInput
    .omit({ positionId: true, memberId: true, price: true })
    .extend({ ...named('set-own-levels'), positionIds: holdingIds }),
  setExitWithRecommendationInput
    .omit({ memberId: true })
    .extend(named('set-exit-with-recommendation')),
  /**
   * `token` is the month the screen offered; refused if the listing moved since.
   * Levels do not change on a roll — new levels are a revision.
   */
  z.object({
    ...named('roll-recommendation'),
    recommendationId: idSchema,
    token: z.number().int().positive(),
    rollOutPrice: price.nullable().default(null),
    rollInPrice: price.nullable().default(null),
  }),
  closeRecommendationInput.extend(named('close-recommendation')),
  setBookTermsFields
    .omit({ memberId: true })
    .extend(named('set-book-terms'))
    .refine(namesATerm, { message: NO_TERM_GIVEN }),
  setMemberTermsFields
    .omit({ memberId: true })
    .extend(named('set-member-terms'))
    .refine(namesAnOverride, { message: NO_TERM_GIVEN }),
  clearBookTermsInput.omit({ memberId: true }).extend(named('clear-book-terms')),
  setAutopilotPausedInput.omit({ memberId: true }).extend(named('set-autopilot-paused')),
  setPlacingInput.omit({ memberId: true }).extend(named('set-placing')),
  submitFeedbackInput.omit({ memberId: true }).extend(named('submit-feedback')),
  z.object(named('refresh-desk-session')),
  z.object({ ...named('run-job'), queue: z.string().min(1).max(64) }),
  z.object({
    ...named('resume-vendor'),
    dependency: z.enum(Object.keys(VENDOR_SITES) as [VendorSite, ...VendorSite[]]),
  }),
  z.object(named('retry-telegram')),
  z.object(named('read-cme')),
  z.object({
    ...named('enter-cme'),
    prices: z
      .record(z.string().max(16), price)
      .refine((prices) => Object.keys(prices).length > 0, { message: 'Enter at least one price.' }),
  }),
  z.object(named('read-circulars-again')),
  z.object(named('reopen-channel')),
  z.object({
    ...named('create-recommendation'),
    fields: addFieldsSchema,
    /** An analyst may only file their own (null); the desk must name one. */
    analystId: idSchema.nullable().default(null),
    confirmNewBook: z.boolean().default(false),
  }),
  editRecommendationInput.extend(named('edit-recommendation')),
  /** A book name, not a ref: the analyst comes from the recommendation, never the browser. */
  z.object({
    ...named('refile-recommendation'),
    recommendationId: idSchema,
    book: z.string().trim().max(32),
    confirmNewBook: z.boolean().default(false),
  }),
  removeRecommendationInput.extend(named('remove-recommendation')),
  decideAccessInput.extend(named('decide-access')),
  /** The session is the browser's own, never the body's. */
  z.object({ ...named('view-as'), memberId: idSchema }),
  z.object(named('stop-viewing-as')),
  // A person's name or bio that will not do is refused, not dropped as a channel post's is.
  applyAnalystInput.omit({ memberId: true }).extend({
    ...named('apply-analyst'),
    name: analystNameSchema.optional(),
    bio: analystBioSchema.optional(),
  }),
  createAnalystInput.omit({ memberId: true }).extend({
    ...named('create-analyst'),
    bio: analystBioSchema.optional(),
    memberEmail: emailSchema.optional(),
  }),
  decideAnalystInput.extend(named('decide-analyst')),
  removeAnalystInput.extend(named('remove-analyst')),
  /** `memberEmail` is desk-only. */
  editAnalystFields
    .omit({ memberId: true })
    .extend({ ...named('edit-analyst'), memberEmail: emailSchema.optional() }),
  addTelegramChannelInput.extend({
    ...named('add-telegram-channel'),
    label: channelLabelSchema.optional(),
  }),
  removeChannelInput.extend(named('remove-channel')),
  /** The analyst is the channel's, never the browser's. */
  setChannelFilingInput.omit({ analystId: true }).extend(named('set-channel-filing')),
  openBookInput.extend(named('open-book')),
  editBookInput.extend(named('edit-book')),
  archiveBookInput.extend(named('remove-book')),
  archiveBookInput.extend(named('archive-book')),
  archiveBookInput.extend(named('restore-book')),
  requestFollowInput.omit({ memberId: true }).extend(named('request-follow')),
  decideFollowInput.extend(named('decide-follow')),
  unfollowInput.omit({ memberId: true }).extend(named('unfollow')),
  mintFollowInviteInput.extend(named('mint-follow-invite')),
  revokeFollowInviteInput.extend(named('revoke-follow-invite')),
  /** The code rides the browser's cookie, never the body, so a page cannot name one. */
  z.object(named('accept-follow-invite')),
  /** `fields` applies only to an `add` draft; the draft keeps the original so the history shows both. */
  z.object({
    ...named('apply-draft'),
    draftId: idSchema,
    book: bookSchema.nullable().default(null),
    fields: addFieldsSchema.nullable().default(null),
    price: price.nullable().default(null),
  }),
  z.object({ ...named('dismiss-draft'), draftId: idSchema }),
  /**
   * `token` is the month shown; refused if the listing moved. `rollOutPrice` records the
   * exit until the fill settles it. The new leg is bought at market: an unfilled limit
   * would leave the member out of the trade.
   */
  z.object({
    ...named('roll-position'),
    positionId: idSchema,
    token: z.number().int().positive(),
    rollOutPrice: price.nullable().default(null),
  }),
  z.object({
    ...named('ask-broker'),
    zerodhaId: z
      .string()
      .trim()
      .toUpperCase()
      .regex(
        /^(?=.{6}$)[A-Z]{2,4}[0-9]{2,4}$/,
        'A Zerodha client ID is six letters and digits, like AB1234.',
      ),
  }),
  /** The secret is sealed on arrival and never read back. */
  z.object({
    ...named('link-broker'),
    memberId: idSchema,
    apiKey: z.string().trim().min(1).max(64),
    apiSecret: z.string().trim().min(1).max(128),
  }),
  z.object(named('unlink-broker')),
  z.object({ ...named('broker-token'), requestToken: requestTokenSchema }),
  /** POST because it mints: a reload must not rotate the URL a chart is already firing at. */
  mintWebhookTokenInput.extend({
    ...named('mint-webhook-token'),
    label: channelLabelSchema.nullish(),
    book: bookSchema.nullish(),
  }),
  /** The only write a denied session may make. */
  z.object(named('request-access')),
  /** Null marks all read, or all `within` holds: the inbox's kinds and scope. */
  z.object({
    ...named('mark-read'),
    ids: z.array(z.number().int()).max(500).nullable(),
    within: z
      .object({
        kinds: z.array(z.enum(NOTIFY_KINDS)).min(1).optional(),
        scope: z.object({ analystId: idSchema, bookId: idSchema.nullable() }).nullable().optional(),
      })
      .nullish(),
  }),
  z.object({ ...named('share-pnl-link'), url: z.string().min(1).max(300) }),
  z.object(named('withdraw-pnl-link')),
  /** Sends no date on purpose: the bot decides which day a sync lands on. */
  z.object(named('sync-pnl')),
  /** Keeps the path's last step: a page read must not write it. */
  z.object(named('see-investments')),
  /** Minting retires the previous code, so it is never minted on a read. */
  z.object(named('link-telegram')),
  z.object(named('test-telegram')),
  z.object(named('unlink-telegram')),
]);

export type WebAction = z.infer<typeof webActionSchema>;

export type WebhookMintRequest = Omit<Extract<WebAction, { do: 'mint-webhook-token' }>, 'do'>;

/**
 * `violations` are the ontology's messages verbatim. `warn`: the write happened, but the
 * message must stay on screen rather than fade.
 */
export const actionDoneSchema = z.object({
  ok: z.literal(true),
  said: z.string(),
  warn: z.boolean().default(false),
  /** What a write hands back beyond its words: a minted URL or link, a count. */
  value: z.unknown().optional(),
});

/** `create-recommendation`'s `value`: the recommendation it filed, so the page can link it. */
export const createdRecommendationSchema = z.object({
  recommendationId: z.number().int().positive(),
});

export type CreatedRecommendation = z.infer<typeof createdRecommendationSchema>;

export const actionReplySchema = z.union([
  actionDoneSchema,
  z.object({ ok: z.literal(false), violations: z.array(z.string()).min(1) }),
]);

export type ActionReply = z.infer<typeof actionReplySchema>;
